/**
 * A fab somebody built themselves.
 *
 * Fab A and Fab B are two halls with the SAME twenty-five tools in them - the
 * comparison they exist to make is about the room, not about the line. This
 * is the other axis: the same room, a line the student chose. A floor plan
 * names which of the twenty-five stations stand on the floor, in what order a
 * wafer meets them, and whether the hall is walled or open.
 *
 * WHY A SUBSET IS A LESSON AND NOT A MUTILATION. A real fab is defined by
 * what it does NOT have. A foundry front end has no crystal puller, because
 * polished wafers arrive on a truck. An OSAT has no lithography at all. A
 * power-discrete line has no damascene loop, because aluminium can be etched
 * and copper cannot. Each of those is a business decision with a process
 * consequence, and the consequence is visible the moment you try to run a
 * route through the floor: some operations have no tool to run on. That is
 * what `flow/coverage.ts` measures, and it is the whole point of letting
 * anyone delete a tool.
 *
 * DATA AND STORAGE ONLY. This module imports nothing else in fab/ on
 * purpose. `hall.ts` reads the active plan to decide which hall to build and
 * `layout.ts` reads it to lay the row out, so anything this file imported
 * from either would close a cycle around the module that runs
 * `applyHallLayout` at import. Tool ids are opaque strings here; layout.ts
 * is what validates them against the catalogue, because layout.ts is what
 * owns the catalogue.
 *
 * Same storage discipline as its neighbours: localStorage behind try/catch,
 * because a private window throws and a classroom machine may have site data
 * off. A plan that cannot be saved is still a plan you can walk around this
 * session; it is only the next session that loses it.
 */

/** Which of the two halls a custom floor is built as. */
export type PlanHall = 'bayChase' | 'ballroom'

export interface FloorPlan {
  /** Stable id. Also half of this fab's hall id - see `hall.ts`. */
  id: string
  /** What the author called it. Theirs, so never translated. */
  name: string
  hall: PlanHall
  /**
   * The stations on this floor, in the order a wafer meets them.
   *
   * Order is not decoration: it is the order of the aisle, and the aisle is
   * the process flow (see layout.ts). A floor whose tools are in route
   * order can be walked as a route; one whose are not is a real fab that
   * grew by accretion, which is also worth seeing.
   */
  tools: string[]
  savedAt: number
}

const STORAGE_KEY = 'simfab.floorplans.v1'

interface Stored {
  plans: FloorPlan[]
  /** The plan the custom hall builds from, when one is being stood in. */
  activeId: string | null
}

const EMPTY: Stored = { plans: [], activeId: null }

/**
 * Shape-check on the way in, because this is the one structure in the app a
 * student could hand-edit in devtools and then walk into. A plan that fails
 * is dropped rather than repaired: a half-understood floor built into the
 * hall is worse than a missing one.
 */
/** A plan-shaped object. Exported because the library (fabLibrary.ts) must believe a document by the same rule. */
export function wellFormed(p: unknown): p is FloorPlan {
  if (!p || typeof p !== 'object') return false
  const q = p as Partial<FloorPlan>
  return (
    typeof q.id === 'string' &&
    q.id.length > 0 &&
    typeof q.name === 'string' &&
    (q.hall === 'bayChase' || q.hall === 'ballroom') &&
    Array.isArray(q.tools) &&
    q.tools.every((t) => typeof t === 'string')
  )
}

function read(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY
    const data = JSON.parse(raw) as Partial<Stored>
    const plans = Array.isArray(data.plans) ? data.plans.filter(wellFormed) : []
    const activeId =
      typeof data.activeId === 'string' && plans.some((p) => p.id === data.activeId)
        ? data.activeId
        : null
    return { plans, activeId }
  } catch {
    return EMPTY
  }
}

let state: Stored = read()

const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

function write(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Not kept for next time; still built for this one.
  }
  notify()
}

/** Told whenever a plan is saved, deleted, or made active. */
export function onPlansChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Every saved floor, newest first. */
export function listPlans(): FloorPlan[] {
  return [...state.plans].sort((a, b) => b.savedAt - a.savedAt)
}

export function getPlan(id: string): FloorPlan | null {
  return state.plans.find((p) => p.id === id) ?? null
}

/** Save a plan, replacing any earlier one with the same id. */
export function savePlan(plan: FloorPlan): void {
  const next = { ...plan, savedAt: Date.now() }
  const i = state.plans.findIndex((p) => p.id === plan.id)
  state = {
    ...state,
    plans: i < 0 ? [...state.plans, next] : state.plans.map((p, j) => (j === i ? next : p)),
  }
  write()
}

/**
 * Forget a floor.
 *
 * A plan that is being STOOD IN is not deleted out from under the person
 * standing in it - the caller has to leave first. Returns false when that is
 * why nothing happened, so the UI can say so rather than silently doing
 * nothing.
 */
export function deletePlan(id: string): boolean {
  if (state.activeId === id) return false
  state = { ...state, plans: state.plans.filter((p) => p.id !== id) }
  write()
  return true
}

/** The plan the custom hall is built from, if a custom hall is standing. */
export function activePlanId(): string | null {
  return state.activeId
}

export function activePlan(): FloorPlan | null {
  return state.activeId ? getPlan(state.activeId) : null
}

/**
 * Point the custom hall at a plan. Does not rebuild anything - `hall.ts`
 * owns that, and calls this on its way into a custom fab.
 */
export function setActivePlanId(id: string | null): void {
  if (state.activeId === id) return
  state = { ...state, activeId: id && getPlan(id) ? id : null }
  write()
}

/**
 * An id for a new floor.
 *
 * Time-based with a random tail: two plans authored in the same millisecond
 * on the same machine is not a scenario, but two authored in the same
 * millisecond in two TABS is, and they share the storage key.
 */
export function newPlanId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
}

/**
 * A fresh name that does not collide with a saved one: "My fab", then "My
 * fab 2". Only ever a starting point - the author renames it.
 */
export function uniqueName(base: string): string {
  const taken = new Set(state.plans.map((p) => p.name))
  if (!taken.has(base)) return base
  for (let n = 2; n < 500; n++) {
    const candidate = `${base} ${n}`
    if (!taken.has(candidate)) return candidate
  }
  return `${base} ${newPlanId()}`
}
